import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import CoachAnalyticsTile from "../components/CoachAnalyticsTile";
import ClubVenuesManager from "../components/ClubVenuesManager";
import ClubEventCalendar from "../components/ClubEventCalendar";
import TournamentClassesEditor, { newClass as newTourClass } from "../components/TournamentClassesEditor";
import EventCardPreview from "../components/EventCardPreview";
import { extractBrandColor } from "../lib/colorFromImage";
import ImageUploader from "../components/ImageUploader";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import DateTimePicker from "../components/DateTimePicker";
import FacilityPicker from "../components/FacilityPicker";
import GolfFormatFields from "../components/GolfFormatFields";

const DEFAULT_GOLF = { format: "stroke", holes: 18, team_size: 1, handicap_allowance: 1.0, course_par: 72, side_games: [] };

export default function ClubDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [manageable, setManageable] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [tab, setTab] = useState("tournament");
  const [tform, setTform] = useState({ sport_id: "", facility_id: null, title: "", description: "", visibility: "public", format: "knockout", max_players: 16, entry_fee: 0, currency: "EUR", start_date: "", end_date: "", registration_opens_at: "", registration_closes_at: "", all_day: false, location: "", cover_url: "", brand_color: null, multi_class_discount_pct: 0, classes: [], golf: null });
  const [lform, setLform] = useState({ sport_id: "", facility_id: null, title: "", description: "", visibility: "public", season_name: "2026", entry_fee: 0, currency: "EUR", start_date: "", end_date: "", all_day: false, location: "", golf: null });

  const fetchAll = useCallback(async () => {
    const [{ data: sp }, { data: cl }, { data: mc }, { data: tr }, { data: lg }] = await Promise.all([
      api.get("/sports"), api.get("/clubs"),
      api.get("/me/manageable-clubs").catch(() => ({ data: [] })),
      api.get("/tournaments"), api.get("/leagues"),
    ]);
    setSports(sp); setClubs(cl); setManageable(mc || []); setTournaments(tr); setLeagues(lg);
    if (sp[0]) {
      setTform((f) => {
        const sportId = f.sport_id || sp[0].id;
        const sportSlug = sp.find((s) => s.id === sportId)?.slug;
        return {
          ...f,
          sport_id: sportId,
          // Pre-seed one default class — a tournament with zero classes can't take registrations.
          classes: f.classes && f.classes.length > 0 ? f.classes : [newTourClass(sportSlug)],
        };
      });
      setLform((f) => ({ ...f, sport_id: f.sport_id || sp[0].id }));
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Track which sport each form has selected — auto-populate / clear golf config.
  const tourSport = useMemo(() => sports.find((s) => s.id === tform.sport_id), [sports, tform.sport_id]);
  const leagueSport = useMemo(() => sports.find((s) => s.id === lform.sport_id), [sports, lform.sport_id]);
  const isTourGolf = tourSport?.slug === "golf";
  const isLeagueGolf = leagueSport?.slug === "golf";
  useEffect(() => {
    setTform((f) => isTourGolf && !f.golf ? { ...f, golf: { ...DEFAULT_GOLF } } : (!isTourGolf && f.golf ? { ...f, golf: null } : f));
  }, [isTourGolf]);
  useEffect(() => {
    setLform((f) => isLeagueGolf && !f.golf ? { ...f, golf: { ...DEFAULT_GOLF } } : (!isLeagueGolf && f.golf ? { ...f, golf: null } : f));
  }, [isLeagueGolf]);

  const submitTour = async (e) => {
    e.preventDefault();
    if (!tform.sport_id) { toast.error("Pick a sport first"); return; }
    if (!tform.start_date || !tform.end_date) { toast.error("Pick start and end dates"); return; }
    // Classes are required for non-golf tournaments. Golf uses formats (Stroke / Stableford / Ryder Cup), not classes.
    if (!isTourGolf) {
      if (!tform.classes || tform.classes.length === 0) { toast.error("Add at least one class"); return; }
      const blankIdx = tform.classes.findIndex((c) => !(c.title || "").trim());
      if (blankIdx !== -1) { toast.error(`Class #${blankIdx + 1} needs a title`); return; }
    }
    // Normalise registration windows (optional) → ISO; drop empty strings
    const regOpens  = tform.registration_opens_at  ? new Date(tform.registration_opens_at).toISOString()  : null;
    const regCloses = tform.registration_closes_at ? new Date(tform.registration_closes_at).toISOString() : null;
    try {
      await api.post("/tournaments", {
        ...tform,
        max_players: Number(tform.max_players) || 16,
        entry_fee: Number(tform.entry_fee) || 0,
        start_date: new Date(tform.start_date).toISOString(),
        end_date: new Date(tform.end_date).toISOString(),
        registration_opens_at: regOpens,
        registration_closes_at: regCloses,
        multi_class_discount_pct: !isTourGolf && tform.multi_class_discount_pct ? Number(tform.multi_class_discount_pct) : null,
        classes: isTourGolf ? [] : (tform.classes || []).map((c) => ({
          ...c,
          capacity: Number(c.capacity) || 16,
          price: Number(c.price) || 0,
          age_min: c.age_min ?? null,
          age_max: c.age_max ?? null,
          skill_rating_min: c.skill_rating_min ?? null,
          skill_rating_max: c.skill_rating_max ?? null,
        })),
        golf: isTourGolf ? tform.golf : null,
      });
      toast.success("Tournament created");
      // Reset the classes list to one fresh blank class so the form is ready for the next tournament.
      const sportSlug = sports.find((s) => s.id === tform.sport_id)?.slug;
      setTform((f) => ({ ...f, title: "", description: "", classes: [newTourClass(sportSlug)] }));
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const submitLeague = async (e) => {
    e.preventDefault();
    if (!lform.sport_id) { toast.error("Pick a sport first"); return; }
    if (!lform.start_date || !lform.end_date) { toast.error("Pick start and end dates"); return; }
    try {
      await api.post("/leagues", { ...lform, entry_fee: Number(lform.entry_fee), start_date: new Date(lform.start_date).toISOString(), end_date: new Date(lform.end_date).toISOString(), golf: isLeagueGolf ? lform.golf : null });
      toast.success("League created"); fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppLayout>
      <h1 className="font-heading text-3xl sm:text-4xl font-black">Club {t("dashboard")}</h1>
      <p className="text-zinc-400 mt-2">Run events, fill courts, manage members.</p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="club-kpis">
        <Kpi label={t("clubs")} value={clubs.length} />
        <Kpi label={t("tournaments")} value={tournaments.length} />
        <Kpi label={t("leagues")} value={leagues.length} />
        <Kpi label={t("revenue")} value="—" />
      </div>

      <div className="mt-6">
        <ClubVenuesManager
          clubs={manageable}
          isAdmin={user?.role === "admin" || user?.role === "federation"}
        />
      </div>

      <div className="mt-6">
        <ClubEventCalendar clubs={manageable} />
      </div>

      <div className="mt-6">
        <CoachAnalyticsTile windowHours={48} kind="any" />
      </div>

      <div className="mt-6 flex gap-2" data-testid="club-tabs">
        <button data-testid="tab-tournament" onClick={() => setTab("tournament")} className={`pp-chip ${tab === "tournament" ? "pp-chip-active" : ""}`}>{t("tournaments")}</button>
        <button data-testid="tab-league" onClick={() => setTab("league")} className={`pp-chip ${tab === "league" ? "pp-chip-active" : ""}`}>{t("leagues")}</button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {tab === "tournament" ? (
          <form onSubmit={submitTour} className="pp-card-elevated p-6 space-y-3" data-testid="club-create-tour">
            <h3 className="font-heading text-xl font-bold">New Tournament</h3>
            <select className="pp-input" data-testid="ct-sport" value={tform.sport_id} onChange={(e) => setTform((f) => ({ ...f, sport_id: e.target.value }))}>{sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            <input className="pp-input" data-testid="ct-title" required value={tform.title} onChange={(e) => setTform((f) => ({ ...f, title: e.target.value }))} placeholder={t("title")} />
            <textarea className="pp-input" data-testid="ct-desc" rows={2} value={tform.description} onChange={(e) => setTform((f) => ({ ...f, description: e.target.value }))} placeholder={t("description")} />
            <FacilityPicker
              sportSlug={(sports.find((s) => s.id === tform.sport_id) || {}).slug}
              value={{ facility_id: tform.facility_id, location: tform.location }}
              onChange={({ facility_id, location }) => setTform((f) => ({ ...f, facility_id, location }))}
              testIdPrefix="ct-facility"
            />

            {/* Cover image — shown on the tournament card / detail header */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Cover image (optional)</label>
              <ImageUploader
                value={tform.cover_url}
                onUploaded={async (url) => {
                  setTform((f) => ({ ...f, cover_url: url || "", brand_color: url ? f.brand_color : null }));
                  if (url) {
                    const hex = await extractBrandColor(url);
                    if (hex) setTform((f) => ({ ...f, brand_color: hex }));
                  }
                }}
                aspect="wide"
                testId="ct-cover"
                label="cover"
              />
            </div>

            {/* Tournament dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Tournament start</label>
                <DateTimePicker data-testid="ct-start" withTime={!tform.all_day} value={tform.start_date} onChange={(v) => setTform((f) => ({ ...f, start_date: v }))} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Tournament end</label>
                <DateTimePicker data-testid="ct-end" withTime={!tform.all_day} value={tform.end_date} onChange={(v) => setTform((f) => ({ ...f, end_date: v }))} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={tform.all_day} onChange={(e) => setTform((f) => ({ ...f, all_day: e.target.checked }))} data-testid="ct-allday" />
              All-day event
            </label>

            {/* Optional registration window */}
            <details className="rounded-lg border border-white/10 p-3">
              <summary className="text-xs text-zinc-400 cursor-pointer hover:text-white">Registration window (optional)</summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Registration opens</label>
                  <DateTimePicker data-testid="ct-reg-open" withTime value={tform.registration_opens_at} onChange={(v) => setTform((f) => ({ ...f, registration_opens_at: v }))} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Registration closes</label>
                  <DateTimePicker data-testid="ct-reg-close" withTime value={tform.registration_closes_at} onChange={(v) => setTform((f) => ({ ...f, registration_closes_at: v }))} />
                </div>
              </div>
            </details>

            {/* Per-class config — the heart of the multi-class tournament UX.
                Hidden for golf: classes (gender / DUPR / UTR) don't map onto golf,
                where the relevant axis is format (Stroke / Stableford / Ryder Cup). */}
            {!isTourGolf && (
              <TournamentClassesEditor
                classes={tform.classes}
                onChange={(cls) => setTform((f) => ({ ...f, classes: cls }))}
                sportSlug={(sports.find((s) => s.id === tform.sport_id) || {}).slug}
                sports={sports}
                primarySportId={tform.sport_id}
              />
            )}

            {/* Multi-class discount — only relevant when classes are in play */}
            {!isTourGolf && tform.classes.length > 1 && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Multi-class discount (%)</label>
                <input
                  type="number" min={0} max={50} step={0.5}
                  value={tform.multi_class_discount_pct || 0}
                  onChange={(e) => setTform((f) => ({ ...f, multi_class_discount_pct: Number(e.target.value || 0) }))}
                  data-testid="ct-multi-discount"
                  className="pp-input w-32"
                  placeholder="0"
                />
                <span className="text-[11px] text-zinc-500 ms-2">applied when a player registers for ≥ 2 classes</span>
              </div>
            )}

            {/* For non-class events (golf), capture the basic max-players + entry-fee. */}
            {isTourGolf && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Max players</label>
                  <input type="number" min={2} max={256} value={tform.max_players}
                    onChange={(e) => setTform((f) => ({ ...f, max_players: Number(e.target.value || 0) }))}
                    data-testid="ct-max-players" className="pp-input w-full" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Entry fee ({tform.currency})</label>
                  <input type="number" min={0} step={0.5} value={tform.entry_fee}
                    onChange={(e) => setTform((f) => ({ ...f, entry_fee: Number(e.target.value || 0) }))}
                    data-testid="ct-entry-fee" className="pp-input w-full" />
                </div>
              </div>
            )}

            <div className="flex gap-2" data-testid="ct-visibility">
              <button type="button" data-testid="ct-vis-public" onClick={() => setTform((f) => ({ ...f, visibility: "public" }))}
                className={`pp-chip flex-1 ${tform.visibility === "public" ? "pp-chip-active" : ""}`}>Public</button>
              <button type="button" data-testid="ct-vis-private" onClick={() => setTform((f) => ({ ...f, visibility: "private" }))}
                className={`pp-chip flex-1 ${tform.visibility === "private" ? "pp-chip-active" : ""}`}>Private (invite/QR)</button>
            </div>
            {isTourGolf && <GolfFormatFields value={tform.golf} onChange={(g) => setTform((f) => ({ ...f, golf: g }))} variant="tournament" />}
            <button data-testid="ct-submit" className="pp-btn-primary px-5 py-3 rounded-xl">{t("create")}</button>
          </form>
        ) : (
          <form onSubmit={submitLeague} className="pp-card-elevated p-6 space-y-3" data-testid="club-create-league">
            <h3 className="font-heading text-xl font-bold">New League</h3>
            <select className="pp-input" data-testid="cle-sport" value={lform.sport_id} onChange={(e) => setLform((f) => ({ ...f, sport_id: e.target.value }))}>{sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            <input className="pp-input" data-testid="cle-title" required value={lform.title} onChange={(e) => setLform((f) => ({ ...f, title: e.target.value }))} placeholder={t("title")} />
            <textarea className="pp-input" data-testid="cle-desc" rows={2} value={lform.description} onChange={(e) => setLform((f) => ({ ...f, description: e.target.value }))} placeholder={t("description")} />
            <input className="pp-input" data-testid="cle-season" required value={lform.season_name} onChange={(e) => setLform((f) => ({ ...f, season_name: e.target.value }))} placeholder={t("season")} />
            <FacilityPicker
              sportSlug={(sports.find((s) => s.id === lform.sport_id) || {}).slug}
              value={{ facility_id: lform.facility_id, location: lform.location }}
              onChange={({ facility_id, location }) => setLform((f) => ({ ...f, facility_id, location }))}
              testIdPrefix="cle-facility"
            />
            <div className="grid grid-cols-2 gap-3">
              <DateTimePicker data-testid="cle-start" withTime={!lform.all_day} value={lform.start_date} onChange={(v) => setLform((f) => ({ ...f, start_date: v }))} />
              <DateTimePicker data-testid="cle-end" withTime={!lform.all_day} value={lform.end_date} onChange={(v) => setLform((f) => ({ ...f, end_date: v }))} />
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={lform.all_day} onChange={(e) => setLform((f) => ({ ...f, all_day: e.target.checked }))} data-testid="cle-allday" />
              All-day event
            </label>
            <input className="pp-input" data-testid="cle-fee" type="number" min={0} value={lform.entry_fee} onChange={(e) => setLform((f) => ({ ...f, entry_fee: e.target.value }))} placeholder={t("entry_fee")} />
            <div className="flex gap-2" data-testid="cle-visibility">
              <button type="button" data-testid="cle-vis-public" onClick={() => setLform((f) => ({ ...f, visibility: "public" }))}
                className={`pp-chip flex-1 ${lform.visibility === "public" ? "pp-chip-active" : ""}`}>Public</button>
              <button type="button" data-testid="cle-vis-private" onClick={() => setLform((f) => ({ ...f, visibility: "private" }))}
                className={`pp-chip flex-1 ${lform.visibility === "private" ? "pp-chip-active" : ""}`}>Private (invite/QR)</button>
            </div>
            {isLeagueGolf && <GolfFormatFields value={lform.golf} onChange={(g) => setLform((f) => ({ ...f, golf: g }))} variant="league" />}
            <button data-testid="cle-submit" className="pp-btn-primary px-5 py-3 rounded-xl">{t("create")}</button>
          </form>
        )}

        <div className="space-y-6">
          {tab === "tournament"
            ? <EventCardPreview kind="tournament" data={tform} sport={tourSport} />
            : <EventCardPreview kind="league" data={lform} sport={leagueSport} />
          }
          <div className="pp-card p-6">
            <h3 className="font-heading text-xl font-bold">Your Events</h3>
            <div className="mt-3 space-y-2 max-h-[500px] overflow-auto" data-testid="club-events">
              {tournaments.map((tr) => (
                <Link to={`/tournaments/${tr.id}`} key={tr.id} className="pp-card p-3 text-sm block hover:border-[#00E676]/40">
                  <div className="font-bold">{tr.title}</div>
                  <div className="text-xs text-zinc-500">{tr.sport?.name} · Tournament · {new Date(tr.start_date).toLocaleDateString()}</div>
                </Link>
              ))}
              {leagues.map((lg) => (
                <Link to={`/leagues/${lg.id}`} key={lg.id} className="pp-card p-3 text-sm block hover:border-[#00E676]/40">
                  <div className="font-bold">{lg.title}</div>
                  <div className="text-xs text-zinc-500">{lg.sport?.name} · League · {lg.season_name}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Kpi({ label, value }) {
  return <div className="pp-card p-4"><div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div><div className="font-heading text-2xl font-bold mt-1 text-[#007AFF]">{value}</div></div>;
}
